/**
 * React Auth layer for @hostesa/auth-client.
 *
 * Provides:
 * - AuthProvider: wraps the app, holds session + user + membership state.
 * - useAuth(): typed hook for consuming auth state and actions.
 *
 * Design decisions:
 * - Token stored via the injected SessionStore (in-memory by default).
 * - fetchMe is called once on mount when a token exists, and after login.
 * - The React layer is intentionally thin — all auth logic lives in the
 *   framework-agnostic modules (permissions, scope, guards, etc.).
 * - onLogout callback: call this to also clear TanStack Query cache and
 *   disconnect Socket.IO. The auth layer does not import those packages
 *   directly to stay dependency-light.
 *
 * Usage:
 *   const queryClient = new QueryClient();
 *   <AuthProvider
 *     apiClient={apiClient}
 *     sessionStore={sessionStore}
 *     onLogout={() => { queryClient.clear(); realtimeClient.disconnect(); }}
 *   >
 *     <App />
 *   </AuthProvider>
 */

import type { ApiClient, Membership, User } from '@hostesa/api-client';
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LoginCredentials } from '../auth-api';
import { fetchMe, login } from '../auth-api';
import { can, canInScope } from '../guards';
import type { MembershipFilter } from '../membership';
import { getActiveMembership } from '../membership';
import type { Permission } from '../permissions';
import type { Scope } from '../scope';
import type { SessionStore } from '../session';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthState {
  /** The authenticated user, or null when unauthenticated/loading. */
  user: User | null;
  /** All memberships returned by GET /me. */
  memberships: Membership[];
  /**
   * The active membership, resolved from `activeMembershipFilter`.
   * null when no filter is set or no membership matches.
   */
  activeMembership: Membership | null;
  /** Opaque session token (treat as secret — never log, never send to analytics). */
  token: string | null;
  /** True while fetchMe is in-flight on mount/login. */
  loading: boolean;
  /** The last auth error (login failure, fetchMe failure). */
  error: Error | null;
  /**
   * Operator impersonation status, surfaced from GET /me. Present (and
   * `active: true`) only while the session token is an operator impersonation
   * token. `label` is a non-PII display string (e.g. "Hotel Admin — Vila Rila").
   */
  impersonation?: { active: boolean; byUserId: string; label: string };
}

export interface AuthActions {
  /** Authenticate with email + password. Stores the token and loads memberships. */
  login(credentials: LoginCredentials): Promise<void>;
  /** Clears the session. Fires the onLogout callback (clear query cache, disconnect realtime). */
  logout(): void;
  /**
   * Sets the active membership filter (e.g. when the user navigates to a
   * tenant/hotel route). Pass null to clear the active membership.
   */
  setActiveMembershipFilter(filter: MembershipFilter | null): void;
  /** RBAC check bound to the active membership's role. */
  can(permission: Permission): boolean;
  /** RBAC + ABAC check bound to the active membership. */
  canInScope(permission: Permission, scope: Scope): boolean;
}

export type AuthContextValue = AuthState & AuthActions;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);
AuthContext.displayName = 'AuthContext';

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface AuthProviderProps {
  children: ReactNode;
  apiClient: ApiClient;
  sessionStore: SessionStore;
  /**
   * Called after logout completes. Use this to clear the TanStack Query cache
   * and disconnect Socket.IO — the auth package does not import those
   * dependencies directly to stay lean.
   *
   * Example:
   *   onLogout={() => { queryClient.clear(); realtimeClient.disconnect(); }}
   */
  onLogout?: () => void;
}

export function AuthProvider({
  children,
  apiClient,
  sessionStore,
  onLogout,
}: AuthProviderProps): ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [token, setTokenState] = useState<string | null>(() => sessionStore.getToken());
  const [loading, setLoading] = useState<boolean>(() => sessionStore.getToken() !== null);
  const [error, setError] = useState<Error | null>(null);
  const [activeMembershipFilter, setActiveMembershipFilter] = useState<MembershipFilter | null>(
    null,
  );
  const [impersonation, setImpersonation] = useState<
    { active: boolean; byUserId: string; label: string } | undefined
  >(undefined);

  // Stable ref to sessionStore to avoid effect re-runs when the store object is re-created.
  const storeRef = useRef(sessionStore);
  storeRef.current = sessionStore;

  // Load the current user once on mount if a token is already stored.
  useEffect(() => {
    const existing = storeRef.current.getToken();
    if (!existing) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchMe(apiClient)
      .then((me) => {
        setUser(me.user);
        setMemberships(me.memberships);
        setImpersonation(me.impersonation);
        setError(null);
      })
      .catch((err: unknown) => {
        // Token expired or invalid — clear session silently.
        storeRef.current.clear();
        setTokenState(null);
        setUser(null);
        setMemberships([]);
        setImpersonation(undefined);
        setError(err instanceof Error ? err : new Error('Session could not be restored.'));
      })
      .finally(() => setLoading(false));
  }, [apiClient]);

  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      setLoading(true);
      setError(null);
      try {
        const result = await login(apiClient, credentials);
        storeRef.current.setToken(result.token);
        setTokenState(result.token);
        // Load full memberships immediately after login.
        const me = await fetchMe(apiClient);
        setUser(me.user);
        setMemberships(me.memberships);
        setImpersonation(me.impersonation);
      } catch (err: unknown) {
        setError(err instanceof Error ? err : new Error('Login failed.'));
        throw err; // re-throw so the login form can handle it
      } finally {
        setLoading(false);
      }
    },
    [apiClient],
  );

  const handleLogout = useCallback(() => {
    storeRef.current.clear();
    setTokenState(null);
    setUser(null);
    setMemberships([]);
    setActiveMembershipFilter(null);
    setImpersonation(undefined);
    setError(null);
    // Notify the app so it can clear the query cache and disconnect realtime.
    onLogout?.();
  }, [onLogout]);

  const activeMembership = useMemo(
    () =>
      activeMembershipFilter !== null
        ? getActiveMembership(memberships, activeMembershipFilter)
        : null,
    [memberships, activeMembershipFilter],
  );

  const canCheck = useCallback(
    (permission: Permission): boolean => {
      if (!activeMembership) return false;
      return can(activeMembership.role, permission);
    },
    [activeMembership],
  );

  const canInScopeCheck = useCallback(
    (permission: Permission, scope: Scope): boolean => {
      if (!activeMembership) return false;
      return canInScope(activeMembership.role, activeMembership, permission, scope);
    },
    [activeMembership],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      activeMembership,
      token,
      loading,
      error,
      impersonation,
      login: handleLogin,
      logout: handleLogout,
      setActiveMembershipFilter,
      can: canCheck,
      canInScope: canInScopeCheck,
    }),
    [
      user,
      memberships,
      activeMembership,
      token,
      loading,
      error,
      impersonation,
      handleLogin,
      handleLogout,
      canCheck,
      canInScopeCheck,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the auth context from the nearest `AuthProvider`.
 * Throws when used outside of an `AuthProvider`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
